import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  SeoPages,
  SeoSettings,
  SeoSettingsPayload,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getSeoSettings,
  updateSeoSettings,
} from '../../../core/services/festival-settings'
import { uploadImage } from '../../../core/services/upload-image'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Button, Card, Input } from '@/components/ui'
import { AlertRatio } from '@/components/AlertRatio'

interface ISEOValues {
  content: Payload[]
}

export interface Payload {
  metaDescription?: string | null
  metaTitle?: string | null
  metaKeywords?: string | null
  metaImages?: string | null
  metaOgImageLink?: string | null
}

const titleStyle = {
  fontSize: '24px',
  fontFamily: 'Helvetica Neue',
  fontWeight: '700',
  lineHeight: '100%',
  fontStyle: 'normal',
}

export const SEOPage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [seo, setSeo] = useState<SeoSettings[]>()
  const [isUploading, setIsUploading] = useState(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [invalidPhoto, setInvalidPhoto] = useState<boolean[]>([false, false]) // add more in case we have more SEOs
  const [images, setImages] = useState<{ name: string; url: string }[]>([
    { name: '', url: '' },
    { name: '', url: '' },
  ])

  const seos: SeoPages[] = ['HOMEPAGE', 'TICKETS']

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      const array: SeoSettings[] = seo || []
      try {
        map(seos, async (s, index) => {
          const response = await getSeoSettings(s)
          array[index] = response.data
          if (response.data.metaOgImageLink) {
            const url = new URL(response.data.metaOgImageLink)
            const i = url?.pathname?.lastIndexOf('/') + 1
            const imageName = url?.pathname?.substring(i)
            const temp = images
            temp[index] = {
              name: imageName,
              url: response.data.metaOgImageLink
            }
            setImages(temp)
            setValue(`content.${index}.metaOgImageLink`, imageName)
            array[index].metaOgImageLink = imageName
          }
          setSeo(array)
          setValue(`content.${index}.metaDescription`, response.data.metaDescription)
          setValue(`content.${index}.metaTitle`, response.data.metaTitle)
          setValue(`content.${index}.metaKeywords`, response.data.metaKeywords)
          setValue(`content.${index}.metaImages`, response.data.metaImages)
        })
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          metaDescription: yup.string().nullable().optional(),
          metaTitle: yup.string().nullable().optional(),
          metaKeywords: yup.string().nullable().optional(),
          metaImages: yup.string().nullable().optional(),
          metaOgImageLink: yup.string().nullable().optional(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm<ISEOValues>({
    mode: 'onChange',
    resolver: yupResolver<ISEOValues>(schema),
  })

  const submit = async (values: ISEOValues, page: SeoPages) => {
    const index = findIndex(seos, (s) => isEqual(s, page))
    const data: SeoSettingsPayload = {
      metaDescription: values.content[index].metaDescription || '',
      metaTitle: values.content[index].metaTitle || '',
      metaKeywords: values.content[index].metaKeywords || '',
      metaImages: values.content[index].metaImages || '',
      metaOgImageKey: values.content[index].metaOgImageLink || '',
    }

    try {
      const { status } = await updateSeoSettings(page, data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async (index: number) => {
    if (seo) {
      setValue(`content.${index}.metaDescription`, seo[index].metaDescription)
      setValue(`content.${index}.metaTitle`, seo[index].metaTitle)
      setValue(`content.${index}.metaKeywords`, seo[index].metaKeywords)
      setValue(`content.${index}.metaImages`, seo[index].metaImages)
      setValue(`content.${index}.metaOgImageLink`, seo[index].metaOgImageLink)
    }
  }

  return (
    <>
      <Header pageTitle="SEO Settings" />
      <Card className="p-4 max-h-[calc(100vh-150px)] overflow-y-auto">
        <PageTitle title="SEO Settings" />
        {map(seos, (s, index) => {
          return (
            <Card className="flex flex-col gap-2 mt-4 p-4" key={s}>
              <span className="text-title-xxs text-accent-1">{s}</span>

              <form
                id={`settings-seo-form-${seos[index]}`}
                onSubmit={handleSubmit((values) => submit(values, seos[index]))}
              >
                <div className="flex flex-col items-start gap-3">
                  <FormGroup
                    className="flex flex-col gap-2 w-full"
                    label="Description"
                  >
                    <Input
                      isInvalid={
                        !!errors?.content?.[index]?.metaDescription?.message
                      }
                      {...register(`content.${index}.metaDescription`)}
                      type="text"
                      placeholder="Enter Description"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup
                    className="flex flex-col gap-2 w-full"
                    label="Meta Title"
                  >
                    <Input
                      isInvalid={!!errors?.content?.[index]?.metaTitle?.message}
                      {...register(`content.${index}.metaTitle`)}
                      type="text"
                      placeholder="Enter Meta Title"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup
                    className="flex flex-col gap-2 w-full"
                    label="Meta Keywords"
                  >
                    <Input
                      isInvalid={
                        !!errors?.content?.[index]?.metaKeywords?.message
                      }
                      {...register(`content.${index}.metaKeywords`)}
                      type="text"
                      placeholder="Enter Meta Keywords"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup
                    className="flex flex-col gap-2 w-full"
                    label="Default Meta For Iamge"
                  >
                    <Input
                      isInvalid={
                        !!errors?.content?.[index]?.metaImages?.message
                      }
                      {...register(`content.${index}.metaImages`)}
                      type="text"
                      placeholder="Enter Default Meta For Iamge"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormUploadPreview
                    control={
                      <Input
                        multiple={false}
                        accept="image/*"
                        disabled={!hasEditPermission}
                        isInvalid={
                          !!errors?.content?.[index]?.metaOgImageLink
                            ?.message || invalidPhoto[index]
                        }
                        name="infoImage"
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const file = e?.target?.files?.[0]
                          if (file) {
                            if (file.size > 1_000_000) {
                              const temp = invalidPhoto
                              temp[index] = true
                              setInvalidPhoto(temp)
                              setIsError(true)
                              setShowMessageBox(true)
                              setMessage('The photo is too large (1 MB)')
                              return
                            }
                            const { photoUrl, photoKey } = await uploadImage(
                              file,
                              setIsUploading,
                              setIsError,
                              setShowMessageBox,
                              setMessage,
                              setIsErrorRatio,
                              'SEO_SETTINGS_DEFAULT_META',
                              false,
                            )

                            if (photoUrl && photoKey && file.name) {
                              setValue(
                                `content.${index}.metaOgImageLink`,
                                photoKey as string,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                },
                              )
                              const temp = images
                              temp[index] = {
                                name: photoKey,
                                url: photoUrl,
                              }
                              setImages(temp)
                            } else {
                              const temp = invalidPhoto
                              temp[index] = true
                              setInvalidPhoto(temp)
                            }
                          }
                        }}
                        type="file"
                      />
                    }
                    isUploading={isUploading}
                    imageName={images?.[index]?.name}
                    url={images?.[index]?.url}
                    onRemove={() => {
                      const temp = images
                      temp[index] = {
                        name: '',
                        url: '',
                      }
                      setImages(temp)
                    }}
                    imageStyle={{
                      objectFit: 'contain',
                    }}
                    label="Default Meta og:image (1200X600)"
                    previewLabel="Default Meta og:image (1200X600)"
                  />
                  {isErrorRatio && invalidPhoto[index] && <AlertRatio />}
                </div>
              </form>

              <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
                <Button
                  onClick={() => handleCancel(index)}
                  variant="secondary"
                  data-dismiss="modal"
                  disabled={!hasEditPermission}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  form={`settings-seo-form-${seos[index]}`}
                  type="submit"
                  disabled={!hasEditPermission}
                >
                  Save
                </Button>
              </div>
            </Card>
          )
        })}
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
